import { LinkKind } from "@gigsync/protocol";
import { Collection } from "../ports.js";
import { NoteFrom } from "./types.js";
/** At most this many notes are kept per link; the oldest drop off, so one chatty person cannot fill a phone. */
const MAX_NOTES_KEPT = 500;
const byTime = (a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id);
/** A link this build opened, as opposed to one made in person by an older build. */
export const isMentorLink = (l) => l.kind === LinkKind.enum.mentor;
/** Mentor links, their notes and the links that have ended, over the Storage port. Never leaves the phone. */
export class LinkStore {
    storage;
    queue = Promise.resolve();
    constructor(storage) {
        this.storage = storage;
    }
    get(user) {
        return this.storage.get(Collection.mentorLinks, user);
    }
    list() {
        return this.storage.list(Collection.mentorLinks);
    }
    /** Changes one link in place; `change` returning undefined leaves it as it was. */
    update(user, change) {
        return this.exclusive(async () => {
            const next = change(await this.get(user));
            if (next)
                await this.save(next);
            return next;
        });
    }
    /**
     * Ends a link here: the link and every note on it go. A mentor link is remembered as past (who they are, when it
     * ended), so this owner can still introduce them; a link an older build made in person is simply forgotten.
     */
    remove(user, now) {
        return this.exclusive(async () => {
            const link = await this.get(user);
            if (link && isMentorLink(link)) {
                const past = { user, card: link.card, ended_at: now.toISOString() };
                await this.storage.put(Collection.pastLinks, user, past);
            }
            await this.storage.delete(Collection.mentorLinks, user);
            await this.storage.delete(Collection.linkNotes, user);
        });
    }
    /** Mentor links that have ended on this phone. */
    past() {
        return this.storage.list(Collection.pastLinks);
    }
    async notes(user) {
        return [...((await this.storage.get(Collection.linkNotes, user)) ?? [])].sort(byTime);
    }
    /** Keeps one note; a note from them also counts as unread until the owner opens the link. */
    addNote(user, note) {
        return this.exclusive(async () => {
            const kept = [...(await this.notes(user)), note].sort(byTime).slice(-MAX_NOTES_KEPT);
            await this.storage.put(Collection.linkNotes, user, kept);
            const link = await this.get(user);
            if (link && note.from === NoteFrom.them)
                await this.save({ ...link, unread: link.unread + 1 });
        });
    }
    save(l) {
        return this.storage.put(Collection.mentorLinks, l.user, l);
    }
    /** Read-modify-write steps run one at a time, so a note arriving while the owner sends one loses neither. */
    exclusive(step) {
        const run = this.queue.then(step, step);
        this.queue = run.catch(() => undefined);
        return run;
    }
}
//# sourceMappingURL=link-store.js.map